/** JWT storage + decoding helpers for the portal.
 *
 * We keep the token in sessionStorage (per-tab, cleared on close)
 * rather than localStorage so a shared browser doesn't leak an admin
 * session to the next visitor. The tradeoff is that a refresh in a
 * new tab requires re-login — acceptable for a low-frequency
 * management tool.
 *
 * Claims decoded here mirror the backend's `create_access_token_for`
 * shape: {sub, role, enterprise_id, scopes: [{scope_type, scope_id}]}.
 */

import { queryClient } from "./queryClient";

const STORAGE_KEY = "agrilens.portal.jwt";

export interface Scope {
  scope_type: "enterprise" | "state" | "district" | "godown";
  scope_id: number;
}

export type Role =
  | "agrilens_super"
  | "enterprise_admin"
  | "state_head"
  | "district_head"
  | "godown_qc_agent"
  | "customer";

export interface TokenClaims {
  sub: string;         // user id as string
  email?: string;
  role: Role;
  enterprise_id: number | null;
  scopes: Scope[];
  exp: number;
}

export function saveToken(token: string): void {
  // Starting a session (login / accept-invite): wipe any cached data
  // from a previous user so nothing bleeds across accounts. Several
  // queries (e.g. the profile /me) are held with staleTime: Infinity,
  // so without this the next user could briefly see the last one's
  // details.
  queryClient.clear();
  sessionStorage.setItem(STORAGE_KEY, token);
}

export function loadToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEY);
}

export function clearToken(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  // Ending a session (sign out): drop every cached query so the next
  // login starts from a clean slate.
  queryClient.clear();
}

/** Decode a JWT without verifying — we only use this for routing
 * decisions in the UI. The backend re-verifies on every request.
 *
 * `scopes` is normalised to always be an array: older tokens (and
 * tokens minted before the backend added a `scopes` claim) omit it,
 * and every caller does `claims.scopes.find(...)` / `.filter(...)`.
 * Without this guard a scopes-less token throws "Cannot read
 * properties of undefined (reading 'find')" inside Layout, which
 * unmounts the whole app to a blank page right after login. */
export function decodeClaims(token: string): TokenClaims | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // Base64URL → base64 padded, then atob.
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded)) as TokenClaims;
    return {
      ...parsed,
      scopes: Array.isArray(parsed.scopes) ? parsed.scopes : [],
    };
  } catch {
    return null;
  }
}

/** True if `token` exists AND its exp claim is in the future. */
export function isValidToken(token: string | null): boolean {
  if (!token) return false;
  const claims = decodeClaims(token);
  if (!claims) return false;
  return claims.exp * 1000 > Date.now();
}

export function currentClaims(): TokenClaims | null {
  const t = loadToken();
  return t && isValidToken(t) ? decodeClaims(t) : null;
}
